import type { ConsentSnapshot, ConsentStorage } from './types';

const CONSENT_KEY = 'cookie_consent_v1';
const RETRY_QUEUE_KEY = 'cookie_consent_retry_queue_v1';

/**
 * In-memory storage — used in tests and SSR environments where localStorage is unavailable.
 */
export class MemoryStorage implements ConsentStorage {
  private store: Map<string, string> = new Map();

  get(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  set(key: string, value: string): void {
    this.store.set(key, value);
  }

  remove(key: string): void {
    this.store.delete(key);
  }
}

/**
 * localStorage adapter for browser environments.
 * Returns MemoryStorage fallback if localStorage is unavailable (SSR, private mode).
 */
export function createBrowserStorage(): ConsentStorage {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return new MemoryStorage();
  }
  try {
    // Probe: will throw in some private modes
    localStorage.setItem('__probe__', '1');
    localStorage.removeItem('__probe__');
    return {
      get: (key) => localStorage.getItem(key),
      set: (key, value) => localStorage.setItem(key, value),
      remove: (key) => localStorage.removeItem(key),
    };
  } catch {
    return new MemoryStorage();
  }
}

export interface RetryQueueItem {
  snapshot: ConsentSnapshot;
  method: 'POST' | 'PUT';
  sessionId?: string;
  attempt: number;
  enqueuedAt: string;
}

export interface ConsentStorageAdapter {
  saveConsent(snapshot: ConsentSnapshot): void;
  loadConsent(): ConsentSnapshot | null;
  clearConsent(): void;
  isConsentExpiredByTime(snapshot: ConsentSnapshot): boolean;
  saveRetryQueue(queue: RetryQueueItem[]): void;
  loadRetryQueue(): RetryQueueItem[];
  clearRetryQueue(): void;
}

/**
 * Creates a typed consent storage adapter wrapping a ConsentStorage backend.
 */
export function createLocalStorageAdapter(storage: ConsentStorage): ConsentStorageAdapter {
  return {
    saveConsent(snapshot: ConsentSnapshot): void {
      storage.set(CONSENT_KEY, JSON.stringify(snapshot));
    },

    loadConsent(): ConsentSnapshot | null {
      const raw = storage.get(CONSENT_KEY);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as ConsentSnapshot;
      } catch {
        return null;
      }
    },

    clearConsent(): void {
      storage.remove(CONSENT_KEY);
    },

    isConsentExpiredByTime(snapshot: ConsentSnapshot): boolean {
      const expiresAt = new Date(snapshot.expiresAt).getTime();
      return expiresAt <= Date.now();
    },

    saveRetryQueue(queue: RetryQueueItem[]): void {
      storage.set(RETRY_QUEUE_KEY, JSON.stringify(queue));
    },

    loadRetryQueue(): RetryQueueItem[] {
      const raw = storage.get(RETRY_QUEUE_KEY);
      if (!raw) return [];
      try {
        return JSON.parse(raw) as RetryQueueItem[];
      } catch {
        return [];
      }
    },

    clearRetryQueue(): void {
      storage.remove(RETRY_QUEUE_KEY);
    },
  };
}
